import {
  razorpayCheckoutEnabled, installmentsNumber, disableButton, enableButton,
  displayFlashErrorMessage, removeFlashErrorMessage, paymentFormPreventSubmitOnEnterKey,
  transactionUUID,
} from './checkoutHelpers';
import { showLoadingSpinner, hideLoadingSpinner } from './loadingSpinner';
import bootstrapper from '../bootstrapper';
import sendTrackpointEvent from '../trackpoint/sendEvent';
import { clientSideFailedPaymentPayload, createFailedPayment } from './createFailedPayment';
import updateFailedPayment from './updateFailedPayment';

let failedAttemptPaymentId;

// Prefer checkout form token, then meta (csrf_meta_tags), then any hidden field—avoid using
// the first authenticity_token on the page (can be another form; breaks after long Razorpay modal).
const csrfToken = () => {
  const fromPaymentForm = document.querySelector('#payment-form input[name="authenticity_token"]')?.value;
  if (fromPaymentForm) return fromPaymentForm;

  const fromMeta = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
  if (fromMeta) return fromMeta;

  return document.querySelector('input[name="authenticity_token"]')?.value;
};

const razorpayRequest = async (body, action, url) => {
  const token = csrfToken();

  return fetch(url, {
    method: action,
    headers: {
      Accept: 'application/json',
      'Content-type': 'application/json; charset=UTF-8',
      'X-CSRF-Token': token,
    },
    body: JSON.stringify(body),
  });
};

const afterModalClosedRequest = async (razorpayInfo, data) => {
  const requestData = {
    url: `${window.location.origin}${data.success_callback_path}`,
    method: 'PATCH',
  };

  const response = await razorpayRequest(razorpayInfo, requestData.method, requestData.url);
  window.location = response.url;
};

const closedModalRedirections = (paymentInstallmentId) => {
  if (window.location.href === `${window.location.origin}/payment_installments/${paymentInstallmentId}/payments/new`) {
    window.location = `${window.location.origin}/${bootstrapper.locale}/enrollments/`;
  } else {
    window.location.reload();
  }
};

const createFailedPaymentWithError = async (error) => {
  const body = clientSideFailedPaymentPayload('razorpay', error, null, installmentsNumber());
  createFailedPayment(body);
};

const updateFailedPaymentAndRedirect = async (data) => {
  const paymentId = data.payment_id;
  const paymentInstallmentId = data.payment_installment_id;

  await updateFailedPayment(paymentId).then((response) => {
    if (!response.ok) {
      throw new Error(response);
    }
    closedModalRedirections(paymentInstallmentId);
  });
};

const requestPayload = data => ({
  key: bootstrapper.razorpayErulearningIn,
  amount: data.amount,
  currency: data.currency,
  name: data.title_message,
  order_id: data.razorpay_order_id,
  payment_id: data.payment_id,
  payment_installment_id: data.payment_installment_id,
  timeout: parseInt(bootstrapper.razorpayModalTimeout, 10),
  handler (response) {
    showLoadingSpinner();
    afterModalClosedRequest(response, data);
  },
  modal: {
    ondismiss () {
      showLoadingSpinner();
      updateFailedPaymentAndRedirect(data);
    },
  },
  prefill: {
    name: data.name,
    email: data.email,
  },
  readonly: { email: true },
  remember_customer: false,
});

const couponCode = () => {
  const couponField = document.querySelector('.js-coupon-code-field');
  const coupon = couponField?.classList.contains('filled') ? couponField?.value : '';

  return coupon;
};

const createRazorpayOrderPayload = () => {
  const payload = {
    coupon: couponCode(),
    shop_payment_installment: {
      number_of_installments: installmentsNumber(),
      transaction_uuid: transactionUUID(),
    },
  };

  return payload;
};

const createRazorpayOrderRequest = async () => {
  const paymentForm = document.getElementById('payment-form');
  const paymentInstallmentUrl = paymentForm.action;
  const body = createRazorpayOrderPayload();
  const response = await razorpayRequest(body, 'POST', paymentInstallmentUrl);
  const responseData = await response.json();

  if (response.ok) return responseData;

  throw new Error(responseData.error);
};

const openRazorpayModal = (data) => {
  const modalData = requestPayload(data);
  const razorpayModal = new window.Razorpay(modalData);
  razorpayModal.on('payment.failed', (response) => {
    const paymentId = response?.error?.metadata?.payment_id;
    if (paymentId !== failedAttemptPaymentId) {
      createFailedPaymentWithError(response.error);
      failedAttemptPaymentId = paymentId;
    }
  });

  razorpayModal.open();
};

const paymentProcess = () => {
  const paymentForm = document.getElementById('payment-form');
  const paymentButtons = Array.from(document.getElementsByClassName('js-razorpay-payment-button'));

  paymentButtons.forEach(
    (paymentButton) => {
      paymentForm.addEventListener('submit', paymentFormPreventSubmitOnEnterKey);

      paymentButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.target.blur();

        showLoadingSpinner();
        disableButton(paymentButton);
        removeFlashErrorMessage();
        createRazorpayOrderRequest()
          .then((res) => {
            openRazorpayModal(res);
          }).catch((err) => {
            displayFlashErrorMessage(err.message);
            enableButton(paymentButton);
          }).finally(() => {
            hideLoadingSpinner();
          });
      });
    },
  );
};

const paymentProcessOnLoad = (razorpayLoader) => {
  const data = razorpayLoader.dataset;
  const razorpayData = JSON.parse(data.razorpay);

  sendTrackpointEvent({
    ...bootstrapper.trackpointMeta,
    event: 'ClickPaymentButton',
  });
  openRazorpayModal(razorpayData);
};

export const razorpayGateway = () => {
  const razorpayLoader = document.getElementById('js-razorpay-modal-loader');

  if (razorpayCheckoutEnabled()) {
    paymentProcess();
  } else if (razorpayLoader) {
    paymentProcessOnLoad(razorpayLoader);
  }
};

export default razorpayGateway;
